import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";

type Industry = {
  id: number;
  code: string;
  name: string;
  order_no: number;
  gtgt_rate: string;
  tncn_rate: string;
  note: string | null;
};

const emptyForm = (): Partial<Industry> => ({
  code: "",
  name: "",
  order_no: 0,
  gtgt_rate: "0",
  tncn_rate: "0",
  note: "",
});

export function IndustriesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<Industry> | null>(null);

  const listQ = useQuery({
    queryKey: ["industries"],
    queryFn: async () => (await api.get<Industry[]>("/industries")).data,
  });

  const save = useMutation({
    mutationFn: async (form: Partial<Industry>) => {
      const payload: any = {
        name: form.name,
        order_no: Number(form.order_no) || 0,
        gtgt_rate: Number(form.gtgt_rate) || 0,
        tncn_rate: Number(form.tncn_rate) || 0,
        note: form.note || null,
      };
      if (form.id) {
        return (await api.patch<Industry>(`/industries/${form.id}`, payload)).data;
      }
      // Code only on create — backend doesn't allow editing
      payload.code = form.code;
      return (await api.post<Industry>("/industries", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["industries"] });
      setEditing(null);
    },
  });

  const del = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/industries/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["industries"] }),
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-semibold text-slate-900">Ngành nghề</h1>
        {isAdmin && (
          <button
            onClick={() => setEditing(emptyForm())}
            className="btn-primary"
          >
            + Thêm ngành
          </button>
        )}
      </div>

      <div className="text-sm text-gray-600 mb-3">
        Mỗi ngành có thuế GTGT và TNCN riêng. Đơn POScake được auto-gán ngành dựa vào "Ngành mặc định" của shop hoặc rule mapping.
      </div>

      <table className="w-full bg-white border border-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left px-3 py-2">Mã</th>
            <th className="text-left px-3 py-2">Tên</th>
            <th className="text-right px-3 py-2">Thứ tự</th>
            <th className="text-right px-3 py-2">GTGT</th>
            <th className="text-right px-3 py-2">TNCN</th>
            <th className="text-left px-3 py-2">Ghi chú</th>
            {isAdmin && <th className="text-right px-3 py-2"></th>}
          </tr>
        </thead>
        <tbody>
          {listQ.isLoading && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                Đang tải...
              </td>
            </tr>
          )}
          {listQ.data?.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                Chưa có ngành nghề.
              </td>
            </tr>
          )}
          {listQ.data?.map((i) => (
            <tr key={i.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-mono text-xs">{i.code}</td>
              <td className="px-3 py-2">{i.name}</td>
              <td className="px-3 py-2 text-right">{i.order_no}</td>
              <td className="px-3 py-2 text-right">
                {(Number(i.gtgt_rate) * 100).toFixed(2)}%
              </td>
              <td className="px-3 py-2 text-right">
                {(Number(i.tncn_rate) * 100).toFixed(2)}%
              </td>
              <td className="px-3 py-2 text-xs text-gray-500">{i.note ?? "—"}</td>
              {isAdmin && (
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => setEditing(i)}
                    className="text-blue-600 hover:underline text-xs mr-2"
                  >
                    Sửa
                  </button>
                  <button
                    onClick={() => {
                      if (
                        confirm(
                          `Xoá ngành "${i.name}"?\nĐơn cũ đã gán ngành này vẫn giữ, chỉ ẩn khỏi danh sách chọn.`
                        )
                      )
                        del.mutate(i.id);
                    }}
                    className="text-red-600 hover:underline text-xs"
                  >
                    Xoá
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <EditDrawer
          form={editing}
          onClose={() => setEditing(null)}
          onSave={(f) => save.mutate(f)}
          saving={save.isPending}
          error={save.error as any}
        />
      )}
    </div>
  );
}

function EditDrawer({
  form: initial,
  onClose,
  onSave,
  saving,
  error,
}: {
  form: Partial<Industry>;
  onClose: () => void;
  onSave: (f: Partial<Industry>) => void;
  saving: boolean;
  error: any;
}) {
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);
  const isNew = !form.id;

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-lg bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isNew ? "Thêm ngành nghề" : `Sửa "${form.code}"`}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(form);
          }}
          className="space-y-3"
        >
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Mã *</label>
            <input
              value={form.code ?? ""}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              required
              disabled={!isNew}
              placeholder="vd: BANLE_HH, DICHVU_XD, ..."
              className="w-full border rounded px-2 py-1.5 text-sm font-mono disabled:bg-gray-50"
            />
            {!isNew && (
              <div className="text-xs text-gray-500 mt-0.5">Không sửa được mã.</div>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Tên *</label>
            <input
              value={form.name ?? ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="vd: Phân phối, cung cấp hàng hoá"
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Thứ tự</label>
              <input
                type="number"
                value={form.order_no ?? 0}
                onChange={(e) =>
                  setForm({ ...form, order_no: Number(e.target.value) || 0 })
                }
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">GTGT (%)</label>
              <input
                type="number"
                step="0.01"
                value={Number(form.gtgt_rate ?? 0) * 100}
                onChange={(e) =>
                  setForm({ ...form, gtgt_rate: String((Number(e.target.value) || 0) / 100) })
                }
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">TNCN (%)</label>
              <input
                type="number"
                step="0.01"
                value={Number(form.tncn_rate ?? 0) * 100}
                onChange={(e) =>
                  setForm({ ...form, tncn_rate: String((Number(e.target.value) || 0) / 100) })
                }
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
          </div>

          <div className="text-xs text-gray-500">
            VD: bán lẻ hàng hoá thường là GTGT 1% + TNCN 0.5%. Dịch vụ: GTGT 5% + TNCN 2%.
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Ghi chú</label>
            <textarea
              value={form.note ?? ""}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={2}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
              {error?.response?.data?.detail ?? String(error)}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
